import React, { useState } from 'react';
import { Button, Badge, Modal, LucideIcon, Typography } from './ot.jsx';
import { Card, CardContent } from './card';

 
const EVENT_TYPE_CONFIG = {
  '建立': { icon: 'Plus',          color: 'text-green-600',  bg: 'bg-green-100',  dot: 'bg-green-500',  badge: 'green'  },
  '更新': { icon: 'Pencil',        color: 'text-blue-600',   bg: 'bg-blue-100',   dot: 'bg-blue-500',   badge: 'blue'   },
  '刪除': { icon: 'Trash2',        color: 'text-red-600',    bg: 'bg-red-100',    dot: 'bg-red-500',    badge: 'red'    },
  '審核': { icon: 'ClipboardCheck',color: 'text-purple-600', bg: 'bg-purple-100', dot: 'bg-purple-500', badge: 'purple' },
  '登入': { icon: 'LogIn',         color: 'text-gray-600',   bg: 'bg-gray-100',   dot: 'bg-gray-400',   badge: 'gray'   },
  '上傳': { icon: 'Upload',        color: 'text-amber-600',  bg: 'bg-amber-100',  dot: 'bg-amber-500',  badge: 'amber'  },
  '完成': { icon: 'CheckCircle',   color: 'text-teal-600',   bg: 'bg-teal-100',   dot: 'bg-teal-500',   badge: 'teal'   },
  '通知': { icon: 'Bell',          color: 'text-orange-600', bg: 'bg-orange-100', dot: 'bg-orange-500', badge: 'orange' },
};

const FILTER_OPTIONS = ['全部', ...Object.keys(EVENT_TYPE_CONFIG)];

const DEFAULT_STATS = [
  { value: 0, label: '全部事件', icon: 'Activity',   bgColor: 'bg-blue-50',   iconColor: 'text-blue-500'   },
  { value: 0, label: '今日',     icon: 'Calendar',   bgColor: 'bg-green-50',  iconColor: 'text-green-500'  },
  { value: 0, label: '本週',     icon: 'CalendarDays',bgColor: 'bg-amber-50', iconColor: 'text-amber-500'  },
  { value: 0, label: '本月',     icon: 'CalendarRange',bgColor:'bg-purple-50', iconColor: 'text-purple-500' },
];

const groupByDate = (events) => {
  const groups = {};
  events.forEach(ev => {
    const key = ev.date || '未知日期';
    if (!groups[key]) groups[key] = [];
    groups[key].push(ev);
  });
  return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
};

const Name32 = ({
  events: propsEvents = [],
  stats: propsStats = null,
  propsCategoryName = '時間軸',
}) => {
  const [filter, setFilter] = useState('全部');
  const [detailEvent, setDetailEvent] = useState(null);

  const displayStats = propsStats || DEFAULT_STATS;

  const filtered = propsEvents.filter(e => filter === '全部' || e.type === filter);
  const groups = groupByDate(filtered);

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between">
        <Typography.Title level={3} style={{ margin: 0 }}>{propsCategoryName}活動記錄</Typography.Title>
        <Button icon={<LucideIcon name="RefreshCw" className="w-4 h-4" />}>
          重新整理
        </Button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {displayStats.map((stat, idx) => (
          <Card key={idx} className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className={`flex items-center gap-3 ${stat.bgColor} rounded-xl p-3`}>
                <LucideIcon name={stat.icon} className={`w-8 h-8 ${stat.iconColor}`} />
                <div>
                  <div className="text-2xl font-bold text-gray-800">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {FILTER_OPTIONS.map(opt => (
          <button
            key={opt}
            onClick={() => setFilter(opt)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === opt ? 'bg-blue-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-300'}`}
          >
            {opt}
          </button>
        ))}
      </div>

      {/* Timeline */}
      {groups.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <LucideIcon name="Activity" className="w-14 h-14 mb-3" />
              <p className="text-sm">尚無活動記錄</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            {groups.map(([date, events], gIdx) => (
              <div key={date} className={gIdx > 0 ? 'mt-8' : ''}>
                {/* Date group header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-sm font-semibold text-gray-700">{date}</span>
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-400">{events.length} 筆</span>
                </div>

                {/* Events */}
                <div className="ml-4 pl-6 border-l-2 border-gray-200 space-y-4">
                  {events.map((ev, idx) => {
                    const cfg = EVENT_TYPE_CONFIG[ev.type] || EVENT_TYPE_CONFIG['更新'];
                    return (
                      <div
                        key={idx}
                        className="relative flex items-start gap-4 cursor-pointer group"
                        onClick={() => setDetailEvent(ev)}
                      >
                        {/* Dot on timeline */}
                        <div className={`absolute -left-[33px] top-2.5 w-3 h-3 rounded-full border-2 border-white ${cfg.dot} flex-shrink-0`} />
                        <div className={`w-9 h-9 rounded-xl ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                          <LucideIcon name={cfg.icon} className={`w-4 h-4 ${cfg.color}`} />
                        </div>
                        <div className="flex-1 min-w-0 pt-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-gray-800 group-hover:text-blue-600 transition-colors">{ev.title}</span>
                            <Badge color={cfg.badge}>{ev.type}</Badge>
                          </div>
                          {ev.description && <p className="text-xs text-gray-500 mt-0.5">{ev.description}</p>}
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                            {ev.actor && <span>{ev.actor}</span>}
                            {ev.time && <span>{ev.time}</span>}
                            {ev.target && <span className="text-gray-500">{ev.target}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Modal
        open={!!detailEvent}
        title="事件詳情"
        onCancel={() => setDetailEvent(null)}
        footer={<Button onClick={() => setDetailEvent(null)}>關閉</Button>}
        width={420}
      >
        {detailEvent && (
          <div className="space-y-3 pt-1">
            <div className="flex items-center gap-2">
              <Badge color={EVENT_TYPE_CONFIG[detailEvent.type]?.badge}>{detailEvent.type}</Badge>
              {detailEvent.time && <span className="text-xs text-gray-400">{detailEvent.date} {detailEvent.time}</span>}
            </div>
            <h3 className="text-base font-bold text-gray-800">{detailEvent.title}</h3>
            {detailEvent.description && <p className="text-sm text-gray-600">{detailEvent.description}</p>}
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              {[
                { label: '操作人員', value: detailEvent.actor  },
                { label: '對象',     value: detailEvent.target },
                { label: '日期',     value: detailEvent.date   },
                { label: '時間',     value: detailEvent.time   },
              ].map(({ label, value }) => value && (
                <div key={label} className="flex justify-between text-sm">
                  <span className="text-gray-400">{label}</span>
                  <span className="font-medium text-gray-700">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Name32;
